import React, { useMemo } from 'react';

export default function AnalyticsPanel({ data, shiftHours }) {
  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;

    const empShiftCounts = {};
    const empWeekendCounts = {};
    let totalAssignments = 0;

    data.forEach((row) => {
      const isWeekend = (() => {
        const d = new Date(row.date);
        const day = d.getDay();
        return day === 0 || day === 6; // Sun or Sat
      })();

      row.employees.forEach((emp) => {
        empShiftCounts[emp] = (empShiftCounts[emp] || 0) + 1;
        if (isWeekend) {
          empWeekendCounts[emp] = (empWeekendCounts[emp] || 0) + 1;
        }
        totalAssignments += 1;
      });
    });

    const empList = Object.keys(empShiftCounts);
    if (empList.length === 0) return null;

    const counts = empList.map((e) => empShiftCounts[e]);
    const minShifts = Math.min(...counts);
    const maxShifts = Math.max(...counts);
    const avgShifts = (totalAssignments / empList.length).toFixed(1);

    // Standard deviation for fairness metric
    const mean = totalAssignments / empList.length;
    const variance = counts.reduce((acc, c) => acc + Math.pow(c - mean, 2), 0) / empList.length;
    const stdDev = Math.sqrt(variance).toFixed(2);

    let fairnessScore = 'SANGAT SEIMBANG';
    let fairnessColor = 'var(--green)';

    if (stdDev > 1.5) {
      fairnessScore = 'PERLU EVALUASI';
      fairnessColor = 'var(--red)';
    } else if (stdDev > 0.8) {
      fairnessScore = 'CUKUP SEIMBANG';
      fairnessColor = 'var(--yellow)';
    }

    return {
      empShiftCounts,
      empWeekendCounts,
      empList,
      minShifts,
      maxShifts,
      avgShifts,
      stdDev,
      fairnessScore,
      fairnessColor,
      totalAssignments,
    };
  }, [data]);

  if (!stats) return null;

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="analytics-icon">📊</span>
          <span className="analytics-title">ANALISIS BEBAN KERJA & KEADILAN SHIFT</span>
        </div>
        <div
          className="fairness-pill"
          style={{ borderColor: stats.fairnessColor, color: stats.fairnessColor }}
        >
          ● {stats.fairnessScore} (Deviasi: {stats.stdDev})
        </div>
      </div>

      <div className="analytics-metrics-grid">
        <div className="analytics-card">
          <div className="analytics-card-label">RATA-RATA SHIFT</div>
          <div className="analytics-card-value">{stats.avgShifts} <span className="analytics-card-unit">shift/pegawai</span></div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card-label">TOTAL HOURS ALLOCATED</div>
          <div className="analytics-card-value">{stats.totalAssignments * shiftHours} <span className="analytics-card-unit">jam total</span></div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card-label">RENTANG SHIFT (MIN–MAX)</div>
          <div className="analytics-card-value">{stats.minShifts} — {stats.maxShifts} <span className="analytics-card-unit">shift</span></div>
        </div>
      </div>

      <div className="workload-bars-list">
        <div className="workload-list-title">DISTRIBUSI SHIFT PER KARYAWAN:</div>
        {stats.empList.map((emp) => {
          const shiftCount = stats.empShiftCounts[emp];
          const weekendCount = stats.empWeekendCounts[emp] || 0;
          const hours = shiftCount * shiftHours;
          const maxPossible = Math.max(stats.maxShifts, 1);
          const percent = Math.min(100, Math.round((shiftCount / maxPossible) * 100));

          return (
            <div key={emp} className="workload-bar-item">
              <div className="workload-item-header">
                <span className="workload-emp-name">{emp}</span>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {weekendCount > 0 && (
                    <span className="weekend-badge">Weekend: {weekendCount} shift</span>
                  )}
                  <span className="workload-emp-stats">{shiftCount} shift ({hours}h)</span>
                </div>
              </div>
              <div className="workload-track">
                <div className="workload-fill" style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
